import { Box, CheckboxField, ErrorText, FormWrapper, FormWrapperProps, RenderParameters, Spacer } from "epsy-ui-react"
import { FormikValues } from "formik"
import React from "react"
import { Button } from "src/components/Button"
import SuggestedLink from "src/components/SuggestedLink"
import { analytics } from "src/services/analytics"
import { isTextTranslationKey, TextTranslationKey, translateText } from "src/services/translations/textTranslations"
import { getScreenLogo, themeModeMap } from "src/services/whiteLabel"
import { useBackgroundColor } from "src/style/global"
import Footer from "src/views/Footer"
import { DefaultTheme, useTheme } from "styled-components"
import EntryField from "../EntryField"
import {
  DescriptionLogo,
  DescriptionTitle,
  FooterContainer,
  Inner,
  LeftContainer,
  MainContainer,
  RightContainer,
  SubmitErrorContainer,
  TitleText
} from "./component"

type FieldType = "text" | "password" | "checkbox"
interface FieldSchema {
  fieldType: FieldType
  name: string
  label?: TextTranslationKey
  hint?: TextTranslationKey
  children?: React.ReactNode
  trackEvent?: string
  passedProps?: { [key: string]: any }
}
type Field = FieldSchema | React.ReactNode
function isFieldSchema(x: Field): x is FieldSchema {
  return typeof x === "object" && (x as any)?.name
}

function renderFields(fields: Field[], mode: DefaultTheme["mode"], allProps: any) {
  return fields.map((field, index) => {
    if (!isFieldSchema(field)) return field
    const { fieldType, name, label, hint, children, trackEvent, passedProps } = field
    const Comp = fieldType === "checkbox" ? CheckboxField : EntryField
    const props = {
      ...allProps,
      ...(passedProps || {}),
      value: allProps.values[name],
      onChange: allProps.handleChange,
      key: name,
      name,
      type: fieldType,
      label: label ? translateText(label) : "",
      hint: hint && translateText(hint),
      children,
      trackEvent,
      entryType: "input",
      currentHost: themeModeMap[mode]
    }

    return (
      <React.Fragment key={name}>
        {index > 0 ? <Spacer height={10} /> : null}
        <Comp data-cy={name} {...props} />
      </React.Fragment>
    )
  })
}

interface AuthFormProps<Values extends FormikValues>
  extends Omit<FormWrapperProps<Values, TextTranslationKey>, "children"> {
  title: TextTranslationKey | React.ReactNode
  submitLabel: TextTranslationKey
  submitTrackEvent?: string
  suggestedLink?: {
    text: TextTranslationKey | string
    linkText: TextTranslationKey
    to: string
    trackEvent?: string
  }
  fields?: Field[]
  children?: React.ReactNode | React.ReactNode[]
}
const AuthForm = <Values extends FormikValues>(props: AuthFormProps<Values>): JSX.Element => {
  const theme = useTheme()
  const {
    title,
    submitLabel,
    submitTrackEvent,
    suggestedLink,
    fields,
    children,
    initialValues,
    validationSchema,
    onSubmit
  } = props
  useBackgroundColor(theme.colors.white)

  const renderChildren = (params: RenderParameters<Values>): React.ReactNode => {
    const { isSubmitting, submitError } = params

    const allParams = { ...props, ...params }

    const textProps = {
      children: isTextTranslationKey(title) ? translateText(title) : title
    }

    return (
      <MainContainer>
        <LeftContainer>
          <Box>
            <img alt={translateText("AUTH/LOGO/ALT")} src={getScreenLogo()} width="220" />
          </Box>
          <Box>
            <Inner>
              {title ? <TitleText {...textProps} /> : ""}
              <Spacer height={20} />
              {fields ? renderFields(fields, theme.mode, allParams) : null}
              {children}
              <SubmitErrorContainer data-cy="submit-error">
                <ErrorText children={submitError} />
              </SubmitErrorContainer>
              <Button
                fullWidth
                loading={isSubmitting}
                onClick={submitTrackEvent ? () => analytics.track(submitTrackEvent) : undefined}
                size="L"
                type="submit"
                variant="PRIMARY"
              >
                {translateText(submitLabel)}
              </Button>
              {suggestedLink && suggestedLink.text ? (
                <>
                  <Spacer height={24} />
                  <SuggestedLink
                    to={suggestedLink.to}
                    trackEvent={suggestedLink.trackEvent}
                    text={isTextTranslationKey(suggestedLink.text) ? translateText(suggestedLink.text) : ""}
                    linkText={translateText(suggestedLink.linkText)}
                  />
                </>
              ) : null}
              <Spacer height={10} />
            </Inner>
          </Box>
          <FooterContainer>
            <Footer />
          </FooterContainer>
        </LeftContainer>

        <RightContainer data-cy="illustration-container">
          <Box>
            <DescriptionTitle children={translateText("LOGIN/DESCRIPTION")} />
          </Box>

          <Box maxWidth={900} margin={20}>
            <DescriptionLogo />
          </Box>
          <FooterContainer></FooterContainer>
        </RightContainer>
      </MainContainer>
    )
  }

  const formWrapperProps = {
    initialValues,
    children: renderChildren,
    validationSchema,
    onSubmit,
    translateError: translateText
  }

  return <FormWrapper {...formWrapperProps} />
}

export default AuthForm
